"use client";

import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { SDK } from "@/@sdk/sdk";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Trash2,
  Copy,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import type {
  iResendDomainDnsRecord,
  iResendDomainDnsRecordsResponse,
  ResendDomainPurpose,
} from "@/@sdk/resend-emails-configurations/resend-emails-configurations-models";
import type { iResendEmailConfig } from "@/@sdk/brands/brand-model";
import { setBrand } from "@/store/modules/brands/brands.slice";
import { useBrandById } from "@/hooks/use-brands";

const refetchBrand = async (
  dispatch: ReturnType<typeof useDispatch>,
  brandId: string,
) => {
  const res = await SDK.brands.getById(brandId);
  dispatch(setBrand(res));
};

function DnsRecordRow({
  r,
  index,
  onCopy,
  showPriority,
}: {
  r: iResendDomainDnsRecord;
  index: number;
  onCopy: (value: string) => void;
  showPriority: boolean;
}) {
  return (
    <TableRow key={`${r.type}-${r.name}-${index}`}>
      <TableCell>
        <div className="flex items-center gap-1.5 max-w-[200px]">
          <span className="font-mono text-sm break-all truncate" title={r.name}>
            {r.name}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => onCopy(r.name)}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5 max-w-[240px]">
          <span
            className="font-mono text-sm text-muted-foreground truncate"
            title={r.value}
          >
            {r.value}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => onCopy(r.value)}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
      {showPriority && (
        <TableCell className="font-mono text-sm">
          {r.priority != null ? r.priority : "—"}
        </TableCell>
      )}
      <TableCell>
        <span
          className={
            r.status === "verified"
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-muted-foreground"
          }
        >
          {r.status}
        </span>
      </TableCell>
    </TableRow>
  );
}

function DnsRecordsContent({
  records,
  domainStatus,
  onCopy,
}: {
  records: iResendDomainDnsRecord[];
  domainStatus: string;
  onCopy: (value: string) => void;
}) {
  const { txtRecords, mxRecords } = useMemo(() => {
    const txt = records.filter((r) => r.type?.toUpperCase() === "TXT");
    const mx = records.filter((r) => r.type?.toUpperCase() === "MX");
    return { txtRecords: txt, mxRecords: mx };
  }, [records]);

  const hasAny = txtRecords.length > 0 || mxRecords.length > 0;

  return (
    <div className="border-t px-4 py-3 space-y-4">
      <p className="text-xs text-muted-foreground">
        Add these records at your DNS provider. Domain status:{" "}
        <span className="font-medium">{domainStatus}</span>
      </p>

      {txtRecords.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">TXT records</h4>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ width: 100 }}>Name</TableHead>
                  <TableHead style={{ width: 100 }}>Value</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txtRecords.map((r, i) => (
                  <DnsRecordRow
                    key={`txt-${r.name}-${i}`}
                    r={r}
                    index={i}
                    onCopy={onCopy}
                    showPriority={false}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {mxRecords.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">MX records</h4>
          <div className="overflow-x-auto max-w-xl">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ width: 100 }}>Name</TableHead>
                  <TableHead style={{ width: 100 }}>Value</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mxRecords.map((r, i) => (
                  <DnsRecordRow
                    key={`mx-${r.name}-${i}`}
                    r={r}
                    index={i}
                    onCopy={onCopy}
                    showPriority={true}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {!hasAny && (
        <p className="py-2 text-sm text-muted-foreground">
          No DNS records to display.
        </p>
      )}
    </div>
  );
}

const PURPOSE_COPY: Record<
  ResendDomainPurpose,
  {
    emptyDescription: string;
    verifiedDescription: (domain: string) => string;
    unverifiedDescription: (domain: string) => string;
    domainPlaceholder: string;
  }
> = {
  booking: {
    domainPlaceholder: "mail.yourcompany.com",
    emptyDescription:
      "No sending domain configured. Add a domain to send booking emails from your own address.",
    verifiedDescription: (domain) =>
      `Sending domain ${domain} is verified. Booking emails can be sent from this domain.`,
    unverifiedDescription: (domain) =>
      `Domain ${domain} is not verified yet. Add the DNS records below at your DNS provider, then click Verify.`,
  },
  quote: {
    domainPlaceholder: "quotes.yourcompany.com",
    emptyDescription:
      "No quote email domain configured. Add a domain separate from your booking domain to send quotes via Resend.",
    verifiedDescription: (domain) =>
      `Quote sending domain ${domain} is verified. Quote emails can be sent from addresses on this domain.`,
    unverifiedDescription: (domain) =>
      `Domain ${domain} is not verified yet. Add the DNS records below, then click Verify.`,
  },
};

export interface ResendDomainSetupProps {
  brandId: string;
  purpose: ResendDomainPurpose;
  /** Override config instead of reading from brand (optional). */
  config?: iResendEmailConfig;
  onConfigChange?: () => void;
}

export function ResendDomainSetup({
  brandId,
  purpose,
  config: configOverride,
  onConfigChange,
}: ResendDomainSetupProps) {
  const dispatch = useDispatch();
  const brand = useBrandById(brandId);
  const copy = PURPOSE_COPY[purpose];

  const config =
    configOverride ??
    (purpose === "quote"
      ? brand?.emailConfig?.quoteResendEmailConfig
      : brand?.resendEmailConfig);

  const [domainInput, setDomainInput] = useState("");
  const [dnsRecords, setDnsRecords] =
    useState<iResendDomainDnsRecordsResponse | null>(null);
  const [dnsLoading, setDnsLoading] = useState(false);
  const [dnsOpen, setDnsOpen] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(false);

  const onSuccessRefetch = useCallback(() => {
    if (!brandId) return;
    refetchBrand(dispatch, brandId).then(() => onConfigChange?.());
  }, [dispatch, brandId, onConfigChange]);

  const fetchDnsRecords = useCallback(async () => {
    setDnsLoading(true);
    try {
      const data = await SDK.resendEmailsConfigurations.getDomainDnsRecords({
        brandId,
        purpose,
      });
      setDnsRecords(data);
      setDnsOpen(true);
    } catch {
      toast.error("Failed to load DNS records");
    } finally {
      setDnsLoading(false);
    }
  }, [brandId, purpose]);

  const { doAction: addDomain, isProcessing: isAdding } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => (body: { domain: string }) =>
        sdk.resendEmailsConfigurations.addDomain({
          ...body,
          brandId,
          purpose,
        }),
      successMessage: "Domain added. Add the DNS records below to verify.",
      failureMessage: "Failed to add domain",
      onSuccess: onSuccessRefetch,
    }),
    [onSuccessRefetch, brandId, purpose],
  );

  const { doAction: verifyDomain, isProcessing: isVerifying } =
    useSDKActionWithDeps(
      () => ({
        action: (sdk) => () =>
          sdk.resendEmailsConfigurations.verifyDomain({ brandId, purpose }),
        successMessage: "Domain verified successfully",
        failureMessage:
          "Verification failed. Ensure DNS records are set correctly.",
        onSuccess: () => {
          onSuccessRefetch();
          fetchDnsRecords();
        },
      }),
      [onSuccessRefetch, fetchDnsRecords, brandId, purpose],
    );

  const { doAction: removeDomain, isProcessing: isRemoving } =
    useSDKActionWithDeps(
      () => ({
        action: (sdk) => () =>
          sdk.resendEmailsConfigurations.removeDomain({
            confirm: true,
            brandId,
            purpose,
          }),
        successMessage: "Domain removed",
        failureMessage: "Failed to remove domain",
        onSuccess: () => {
          onSuccessRefetch();
          setRemoveConfirm(false);
          setDnsRecords(null);
          setDnsOpen(false);
        },
      }),
      [onSuccessRefetch, brandId, purpose],
    );

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  }, []);

  const handleAddDomain = useCallback(() => {
    const domain = domainInput.trim().toLowerCase();
    if (!domain) {
      toast.error("Enter a domain name");
      return;
    }
    addDomain({ domain }).then(() => setDomainInput(""));
  }, [domainInput, addDomain]);

  const handleRemove = useCallback(() => {
    if (!removeConfirm) {
      setRemoveConfirm(true);
      return;
    }
    removeDomain();
  }, [removeConfirm, removeDomain]);

  if (!config?.domain) {
    return (
      <div className="space-y-4">
        <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/20">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {copy.emptyDescription}{" "}
            <code className="rounded bg-amber-100 px-1 dark:bg-amber-900/40">
              {copy.domainPlaceholder}
            </code>
          </AlertDescription>
        </Alert>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder={copy.domainPlaceholder}
            className="max-w-xs"
            onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
          />
          <Button onClick={handleAddDomain} disabled={isAdding}>
            {isAdding ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              "Add domain"
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {config.isVerified ? (
        <Alert className="border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/20">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          <AlertDescription>
            {copy.verifiedDescription(config.domain)}
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/20">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {copy.unverifiedDescription(config.domain)}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">Domain:</span>
        <code className="rounded bg-muted px-2 py-1 text-sm">{config.domain}</code>
      </div>

      <Collapsible open={dnsOpen} onOpenChange={setDnsOpen}>
        <div className="rounded-lg border bg-muted/30">
          <Button
            variant="ghost"
            className="w-full justify-between px-4 py-3"
            onClick={() => {
              if (dnsRecords === null && !dnsLoading) {
                fetchDnsRecords();
              } else {
                setDnsOpen((o) => !o);
              }
            }}
          >
            <span className="flex items-center gap-2">
              {dnsOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {dnsLoading ? "Loading DNS records..." : "View DNS records"}
            </span>
            {dnsRecords && (
              <span className="text-muted-foreground text-xs">
                {dnsRecords.records.length} record
                {dnsRecords.records.length !== 1 ? "s" : ""}
              </span>
            )}
          </Button>
          <CollapsibleContent>
            {dnsLoading && (
              <div className="flex items-center justify-center gap-2 px-4 py-6">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm text-muted-foreground">
                  Fetching records...
                </span>
              </div>
            )}
            {!dnsLoading && dnsRecords && (
              <DnsRecordsContent
                records={dnsRecords.records}
                domainStatus={dnsRecords.status}
                onCopy={copyToClipboard}
              />
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>

      {!config.isVerified && (
        <Button
          variant="secondary"
          onClick={() => verifyDomain()}
          disabled={isVerifying}
        >
          {isVerifying ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            "Verify domain"
          )}
        </Button>
      )}

      <div className="flex items-center gap-2 pt-2">
        <Button
          variant={removeConfirm ? "destructive" : "outline"}
          size="sm"
          onClick={handleRemove}
          disabled={isRemoving}
        >
          {isRemoving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Removing...
            </>
          ) : removeConfirm ? (
            <>
              <Trash2 className="mr-2 h-4 w-4" />
              Click again to confirm remove
            </>
          ) : (
            <>
              <Trash2 className="mr-2 h-4 w-4" />
              Remove domain
            </>
          )}
        </Button>
        {removeConfirm && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setRemoveConfirm(false)}
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
